import * as React from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '../utils/cn';
import { buttonVariants } from './button';

export const Sheet = SheetPrimitive.Root;
export const SheetTrigger = SheetPrimitive.Trigger;
export const SheetClose = SheetPrimitive.Close;
export const SheetPortal = SheetPrimitive.Portal;

export const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/40 backdrop-blur-sm data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:animate-in data-[state=open]:fade-in',
      className
    )}
    {...props}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

type SheetSize = 'sm' | 'md' | 'lg' | 'full';
type SheetSide = 'top' | 'bottom' | 'left' | 'right';

export const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> & { size?: SheetSize; side?: SheetSide }
>(({ className, children, size = 'md', side = 'right', ...props }, ref) => {
  const sizeClass =
    size === 'sm'
      ? 'sm:max-w-[360px]'
      : size === 'lg'
        ? 'sm:max-w-[880px]'
        : size === 'full'
          ? 'sm:max-w-[100vw]'
          : 'sm:max-w-[560px]';

  const sideClass =
    side === 'left'
      ? 'left-0 right-auto border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left'
      : side === 'top'
        ? 'top-0 bottom-auto h-3/4 w-full border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top'
        : side === 'bottom'
          ? 'bottom-0 top-auto h-3/4 w-full border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom'
          : 'right-0 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right';

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        ref={ref}
        className={cn(
          'fixed inset-y-0 z-50 flex w-full flex-col overflow-y-auto border-border bg-background p-6 shadow-xl transition data-[state=closed]:animate-out data-[state=open]:animate-in',
          sideClass,
          sizeClass,
          className
        )}
        {...props}
      >
        {children}
        <SheetPrimitive.Close
          aria-label="Close"
          className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'absolute right-4 top-4 text-muted-foreground')}
        >
          <X className="h-4 w-4" aria-hidden />
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  );
});
SheetContent.displayName = SheetPrimitive.Content.displayName;

export const SheetHeader = ({ className, ...props }: React.ComponentProps<'div'>) => (
  <div className={cn('space-y-1.5 text-left', className)} {...props} />
);
SheetHeader.displayName = 'SheetHeader';

export const SheetFooter = ({ className, ...props }: React.ComponentProps<'div'>) => (
  <div className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)} {...props} />
);
SheetFooter.displayName = 'SheetFooter';

export const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title ref={ref} className={cn('text-lg font-semibold text-foreground', className)} {...props} />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

export const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;
